import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import DialogContent from '@mui/material/DialogContent'
import Dialog from '@mui/material/Dialog'
import { find, prop, propEq, propOr } from 'ramda'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import TableContainer from '@mui/material/TableContainer'
import Paper from '@mui/material/Paper'
import Checkbox from '@mui/material/Checkbox/Checkbox'
import DialogActions from '@mui/material/DialogActions/DialogActions'
import InfiniteScroll from 'react-infinite-scroll-component'
import { styled } from '@mui/material'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { useField } from 'react-final-form'
import TableHead from '@mui/material/TableHead'
import { useTranslation } from 'react-i18next'

import DialogTitle from '~/components/DialogTitle'
import TextOverflow from '~/components/TextOverflow'
import Avatar from '~/components/Avatar'
import { Button, DiscardButton } from '~/components/Buttons'
import CurrencyMoney from '~/components/Money/CurrencyMoney'
import Status, { ERROR, SUCCESS } from '~/components/Status'
import InfinityScrollSearch from '~/components/InfinityScrollSearch'

import VariantTableSkeletonList from './VariantTableSkeletonList'

const TableRowStyled = styled(TableRow)({
  cursor: 'pointer',
  '&:hover': {
    background: '#0000000A'
  }
})

const DialogContentStyled = styled(DialogContent)({
  padding: '0',
  position: 'relative',
  height: 'calc(100vh / 2)'
})

function SaleOrderVariantAddDialog ({ open, onClose, onAddVariants, params, saleOrderVariant }) {
  const { t } = useTranslation()
  const [selected, setSelected] = useState([])

  const results = propOr([], 'results', saleOrderVariant)
  const count = prop('count', saleOrderVariant)

  const handleCheck = (checked, variant) => {
    if (checked) {
      setSelected(prevSelected => prevSelected.filter(select => prop('guid', select) !== prop('guid', variant)))
    } else {
      setSelected(prevSelected => [...prevSelected, variant])
    }
  }

  const searchField = useField('search')
  const searchFieldValue = searchField.input.value

  const onEscape = useCallback(event => {
    if (event.code === 'Escape') {
      onClose()
    }
  }, [onClose])

  const handleAddVariants = useCallback(() => {
    onAddVariants(selected)
    setSelected([])
  }, [selected, onAddVariants])

  return (
    <Dialog open={open} maxWidth="md" fullWidth={true} onKeyDown={onEscape}>
      <DialogTitle variant="h6">
        {`${t('Browse Variants')} - ${t('Selected')} (${selected.length})`}
        <Box sx={{ padding: '24px 0' }}>
          <InfinityScrollSearch
            onEffect={saleOrderVariant.reset}
            getList={saleOrderVariant.getList}
            params={params}
          />
        </Box>
      </DialogTitle>
      <DialogContentStyled id="saleOrderVariantTable">
        <InfiniteScroll
          dataLength={results.length}
          hasMore={count > results.length}
          loader=""
          next={() => saleOrderVariant.getList({ search: searchFieldValue, ...params })}
          scrollableTarget="saleOrderVariantTable"
        >
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={70}>{t('Select')}</TableCell>
                  <TableCell width={100}>{t('Image')}</TableCell>
                  <TableCell width={250}>{t('Variant')}</TableCell>
                  <TableCell width={150}>{t('SKU')}</TableCell>
                  <TableCell width={100}>{t('Price')}</TableCell>
                  <TableCell width={70}>{t('Available')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((variant, index) => {
                  const variantGuid = prop('guid', variant)
                  const checked = find(propEq('guid', variantGuid), selected)
                  const totalAvailable = propOr(0, 'totalAvailable', variant)
                  const statusColor = totalAvailable > 0 ? SUCCESS : ERROR
                  const src = prop('defaultImage', variant)

                  return (
                    <TableRowStyled key={variantGuid} onClick={() => handleCheck(checked, variant)}>
                      <TableCell data-cy={`variant-${index}`}>
                        <Checkbox checked={Boolean(checked)} color="secondary" />
                      </TableCell>
                      <TableCell>
                        <Avatar
                          alt={prop('name', variant)}
                          src={src}
                          variant="rounded"
                        />
                      </TableCell>
                      <TableCell>
                        <TextOverflow lines={1} selfTooltip={true}>
                          {prop('name', variant)}
                        </TextOverflow>
                      </TableCell>
                      <TableCell>
                        {prop('sku', variant)}
                      </TableCell>
                      <TableCell>
                        <CurrencyMoney value={prop('retailPrice', variant)} />
                      </TableCell>
                      <TableCell>
                        <TextOverflow tooltip="Available quantity" >
                          <Typography variant="body2">
                            <Status label={totalAvailable} color={statusColor} />
                          </Typography>
                        </TextOverflow>
                      </TableCell>
                    </TableRowStyled>
                  )
                })}
                {saleOrderVariant.isLoading && (
                  <VariantTableSkeletonList />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </InfiniteScroll>
      </DialogContentStyled>
      <DialogActions>
        <DiscardButton
          onClick={onClose}
          fullWidth={true}
        >
          Discard
        </DiscardButton>
        <Button
          variant="contained"
          data-cy="add"
          fullWidth={true}
          onClick={handleAddVariants}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  )
}

SaleOrderVariantAddDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAddVariants: PropTypes.func.isRequired,
  params: PropTypes.object,
  saleOrderVariant: PropTypes.object.isRequired
}

export default SaleOrderVariantAddDialog
